import { Box, DialogActions, Stack } from "@mui/material";
import { pictureAspectRatio } from "@src/appV2/Agents/components/ContractorBadge/utils/createBadgePdf";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs/FullScreen/Dialog";
import { LoadingSpinner, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useCallback, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";

import { Button } from "../../components/Button";
import { getSafeAreaPaddings } from "../../utils/getSafeAreaPaddings";
import { getCroppedImageFromArea } from "../utils/getCroppedImageFromArea";

export interface ImageCropperModalProps {
  modalState: UseModalState;
  imageSrc: string;
  onRetry: () => void;
  onCropComplete: (croppedImageBase64: string) => void;
}

export function ImageCropperModal(props: ImageCropperModalProps) {
  const { modalState, imageSrc, onRetry, onCropComplete } = props;

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropCompleteCallback = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleUse = useCallback(async () => {
    if (!croppedAreaPixels) {
      return;
    }

    setIsProcessing(true);
    try {
      const croppedImage = await getCroppedImageFromArea(imageSrc, croppedAreaPixels);
      onCropComplete(croppedImage);
      modalState.closeModal();
    } catch (error) {
      logError(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_TAKE_PHOTO_CROPPING_FAILURE, {
        error,
      });
    } finally {
      setIsProcessing(false);
    }
  }, [croppedAreaPixels, imageSrc, onCropComplete, modalState]);

  const handleRetry = useCallback(() => {
    // Reset state when retrying
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(undefined);
    modalState.closeModal();
    onRetry();
  }, [onRetry, modalState]);

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState}>
      <Box
        sx={{
          flex: 1,
          position: "relative",
          backgroundColor: "common.black",
        }}
      >
        {imageSrc ? (
          <Cropper
            showGrid
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={pictureAspectRatio}
            objectFit="contain"
            style={{
              containerStyle: {
                width: "100%",
                height: "100%",
                backgroundColor: "black",
              },
            }}
            onCropChange={setCrop}
            onCropComplete={onCropCompleteCallback}
            onZoomChange={setZoom}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <LoadingSpinner size="large" variant="invert" />
          </Box>
        )}
      </Box>
      <DialogActions
        sx={(theme) => ({
          backgroundColor: "common.black",
          ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(5) }),
        })}
      >
        <Stack spacing={4} width="100%">
          <Button
            size="medium"
            variant="outlined"
            disabled={!croppedAreaPixels || isProcessing || !imageSrc}
            onClick={handleUse}
          >
            Use
          </Button>
          <Button
            invert
            size="medium"
            variant="outlined"
            disabled={isProcessing}
            onClick={handleRetry}
          >
            Retry
          </Button>
        </Stack>
      </DialogActions>
    </FullScreenDialog>
  );
}
